import { useEffect } from "react";
import AssetType from "../api/models/fixProtocol/instrument/assetType";
import Security from "../api/models/fixProtocol/instrument/security";
import PortfolioComponent from "../api/models/portfolioComponent";
import Position from "../api/models/portfolioPosition";
import RebalanceItem from "../api/models/rebalanceItem";


/** Value format types. e.g. "ROE" is "percentage"; "Net income" is "decimal"; "P/E" is "multipleTimes". */
export type ValueFormat = "percentage" | "decimal" | "multipleTimes";

/**
 * Formats the value using the appropriate formatter for the given format.
 * @param valueFormat The format.
 * @returns The string of the formatted value.
 */
export const FormatValue = (value: number | undefined | null, valueFormat: ValueFormat): string => {
  if (value === undefined || value === null) {
    return "N/D";
  }
  const formatterPercentage = Intl.NumberFormat("pt-BR", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  const formatterDecimal = Intl.NumberFormat("pt-BR", {
    style: "decimal",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return (valueFormat === "percentage") ? formatterPercentage.format(value)
    : (valueFormat === "decimal") ? formatterDecimal.format(value)
      : (valueFormat === "multipleTimes") ? formatterDecimal.format(value) + "x"
        : "N/D";
}

/**
 * Delays for an amount of time.
 * @param ms The time to delay, in miliseconds.
 */
function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Ensure that a discrete amount of time is passed and then resolves the promise.
 * The most common use case is for a more pleasant UX during loading times (avoid fast blinking loading time screens).
 * @param promise The original promise.
 * @param interval The allowed discrete intervals of time.
 * @returns The result of the original promise.
 */
export async function awaitWithDiscreteDelay<T>(promise: Promise<T>, interval = 1500) {
  const start = Date.now();
  const result = await promise;
  const elapsed = Date.now() - start;

  const remainder = elapsed % interval;
  const waitTime = remainder === 0 ? 0 : interval - remainder;

  if (waitTime > 0) {
    await delay(waitTime);
  }

  return result;
}

/**
 * @param rebalanceItem 
 * @returns The most appropriate string representation of the rebalance notional.
 */
export function rebalanceNotionalString(rebalanceItem: RebalanceItem): string {
  return (rebalanceItem.price * rebalanceItem.quantity).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/**
 * @param rebalanceItem 
 * @returns The most appropriate string representation of the rebalance quantity.
 */
export function rebalanceQuantityString(rebalanceItem: RebalanceItem): string {
  if (isFund(rebalanceItem.position.security?.assetType)) {
    return rebalanceItem.quantity.toLocaleString('pt-BR', { minimumFractionDigits: 8, maximumFractionDigits: 8 });
  } else if (isGovernmentBond(rebalanceItem.position.security?.assetType)) {
    return rebalanceItem.quantity.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else {
    return rebalanceItem.quantity.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
}

/**
 * @param position 
 * @returns The most appropriate string representation of the position notional.
 */
export function positionNotionalString(position: Position): string {
  return (position.price * position.quantity).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/**
 * @param position 
 * @returns The most appropriate string representation of the position price.
 */
export function positionPriceString(position: Position): string {
  if (isFund(position.security?.assetType)) {
    return position.price.toLocaleString('pt-BR', { minimumFractionDigits: 7, maximumFractionDigits: 7 });
  } else {
    return position.price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}

/**
 * @param position 
 * @returns The most appropriate string representation of the position quantity.
 */
export function positionQuantityString(position: Position): string {
  if (isFund(position.security?.assetType)) {
    return position.quantity.toLocaleString('pt-BR', { minimumFractionDigits: 8, maximumFractionDigits: 8 });
  } else if (isGovernmentBond(position.security?.assetType)) {
    return position.quantity.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else {
    return position.quantity.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
}

/**
 * @param position 
 * @returns The most appropriate string representation of the component current percentage.
 */
export function componentPercentageString(component: PortfolioComponent): string {
  return (component.currentPercentage() * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
}

/**
 * @param position 
 * @returns The most appropriate string representation of the component ideal percentage.
 */
export function componentIdealPercentageString(component: PortfolioComponent): string {
  return (component.idealPercentage() * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
}

/**
 * @param position 
 * @returns The most appropriate description for the position.
 */
export function positionDescription(position: Position): string {
  return securityDescription(position.security) ?? position.description;
}

/**
 * @param security 
 * @returns The most appropriate description for the security.
 */
export function securityDescription(security?: Security): string | undefined {
  if (isFund(security?.assetType)) {
    return formatFundName(security?.symbol)
  } else {
    return security?.symbol ?? security?.description;
  }
}

/**
 * @param assetType 
 * @returns True if assetType is a fund, false otherwise.
 */
export function isFund(assetType?: AssetType): boolean {
  const assetTypeNames = ['FUNDO DE AÇÃO', 'FUNDO CAMBIAL', 'FUNDO MULTIMERCADO', 'FUNDO DE RENDA FIXA', 'FUNDO DE PREVIDÊNCIA'];
  return assetTypeNames.includes(assetType?.name ?? '');
}

/**
 * @param assetType 
 * @returns True if assetType is a government bond, false otherwise.
 */
export function isGovernmentBond(assetType?: AssetType): boolean {
  const assetTypeNames = ['LTN', 'NTN-F', 'NTN-B', 'NTN-B1', 'NTN-C', 'LFT', 'NTNB PRINC'];
  return assetTypeNames.includes(assetType?.name ?? '');
}

/**
 * Improve readability of fund name.
 * @param fundName 
 * @returns Improved name.
 */
export function formatFundName(fundName?: string): string {
  let formatted = fundName ?? '';

  // TYPO CORRECTION
  formatted = formatted.replace(/FINNCEIRO/gi, 'FINANCEIRO');

  formatted = formatted.replace(/FUNDO(S)? DE INVESTIMENTO(S)?/gi, 'FI');
  formatted = formatted.replace(/FI FINANCEIRO/gi, 'FI');

  formatted = formatted.replace(/FIF( DA| -)? CLASSE DE INVESTIMENTO(S)? EM COTA(S)?/i, 'FIC');
  formatted = formatted.replace(/FI EM COTAS/i, 'FIC');
  formatted = formatted.replace(/FIC DE/i, 'FIC');

  formatted = formatted.replace(/FI EM AÇÕES/i, 'FIA');
  formatted = formatted.replace(/FI RENDA FIXA/i, 'FIRF');
  formatted = formatted.replace(/FI MULTIMERCADO/i, 'FIM');
  formatted = formatted.replace(/FI MULT/i, 'FIM');

  formatted = formatted.replace(/FIC EM AÇÕES/i, 'FIC FIA');
  formatted = formatted.replace(/FIC RENDA FIXA/i, 'FIC FIRF');
  formatted = formatted.replace(/FIC MULTIMERCADO/i, 'FIC FIM');

  formatted = formatted.replace(/FIF EM AÇÕES/i, 'FIC FIA');
  formatted = formatted.replace(/FIF RENDA FIXA/i, 'FIC FIRF');
  formatted = formatted.replace(/FIF MULTIMERCADO/i, 'FIC FIM');
  formatted = formatted.replace(/FIF MULT/i, 'FIC FIM');

  return formatted;
}

/**
 * Format a digits only string as '##.###.###/####-##'
 * @param numberOnlyCnpj Cnpj to be formatted, must be a string containing only number.
 * @returns The formatted cnpj.
 */
export function formatCnpj(numberOnlyCnpj?: string): string {
  if (!numberOnlyCnpj) {
    return numberOnlyCnpj ?? '';
  }
  numberOnlyCnpj = numberOnlyCnpj.padStart(14, '0');
  const pattern = '##.###.###/####-##';
  let i = 0;
  return pattern.replace(/#/g, _ => numberOnlyCnpj[i++]);
}

/**
 * Indicates if it is a mobile.
 * @returns 
 */
export function isMobile(): boolean {
  return window.outerWidth < 1024;
}

/**
 * Set the title of the page for a react component.
 */
export function useTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title])
}